import type { AccountUserWithDetail } from '@/generated/hey-api/types.gen';

/**
 * Wizard step IDs as they appear in the URL (`?step=...`).
 */
export type WizardStep = 'register' | 'client-details' | 'talent-profile' | 'completed';

/**
 * Map a backend `onboarding_step` enum value to the URL step the user should
 * be on. Role is decided at signup (path-derived), so every mid-onboarding
 * state maps straight to the role's details step. Legacy deferred-intent users
 * (role=client, signup_intent=talent, mid-onboarding) land on client-details
 * and can switch via the role-switch link there.
 */
export function stepForOnboardingState(user: AccountUserWithDetail): WizardStep {
	if (user.onboarding_step === 'completed') return 'completed';
	return user.role === 'talent' ? 'talent-profile' : 'client-details';
}

/**
 * Returns the URL the user should be sent to, or `null` when the requested
 * step matches the user's current onboarding progress.
 *
 *  - Completed user → role-aware home (`/talent/jobs` or `/client`)
 *  - Wrong wizard step → `/onboarding?step=<correct>`
 *  - Right step → `null` (no redirect)
 *
 *  `requestedStep === 'register'` (i.e. `/signup`) is always wrong for an
 *  already-logged-in mid-onboarding user - bounce them to their actual step.
 */
export function validateWizardStep(
	user: AccountUserWithDetail,
	requestedStep: WizardStep | undefined,
): { to: '/talent/jobs' } | { to: '/client' } | { to: '/onboarding'; step: WizardStep } | null {
	const expected = stepForOnboardingState(user);

	if (expected === 'completed') {
		return user.role === 'talent' ? { to: '/talent/jobs' } : { to: '/client' };
	}

	if (requestedStep === expected) return null;
	return { to: '/onboarding', step: expected };
}
